import {
    AlternateEmailOutlined,
    BusinessOutlined,
    ManageAccountsOutlined,
    PublicOutlined,
    TuneOutlined,
} from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { ComponentType } from 'react';

import AuFlag from 'assets/flags/AuFlag';
import CaFlag from 'assets/flags/CaFlag';
import UkFlag from 'assets/flags/UkFlag';
import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { ACTIONS } from 'constant';
import {
    formatFeatureName,
    formatRetainerFlowName,
    isRetainerFlowFeature,
    selectedRetainerFlow,
} from 'helpers/serviceProviderFeatures';
import useTranslate from 'hooks/useTranslate';
import type { CountryCode, ServiceProviderAdminRow } from 'types';
import { countryLabelKey, normalizeEnabledCountryCodes } from 'utils/countries';

interface FlagProps {
    width?: string;
    height?: string;
}

const COUNTRY_MARKS: Record<CountryCode, { shortLabel: string; Flag: ComponentType<FlagProps> }> = {
    AU: { shortLabel: 'AUS', Flag: AuFlag },
    CA: { shortLabel: 'CAN', Flag: CaFlag },
    UK: { shortLabel: 'UK', Flag: UkFlag },
};

export type ServiceProviderAction = ACTIONS.EDIT | 'COUNTRIES' | 'DEFAULT_CONSULTANTS' | 'FEATURES';

interface ServiceProvidersTableProps {
    providers: ServiceProviderAdminRow[];
    loading?: boolean;
    handleAction: (action: ServiceProviderAction, provider: ServiceProviderAdminRow) => void;
}

const actionBtnSx = { p: 0.5 };

function FeatureChips({ features }: { features: string[] }) {
    const { translate } = useTranslate();
    const regularFeatures = features.filter((feature) => !isRetainerFlowFeature(feature));

    if (!regularFeatures.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('noFeaturesEnabled')}
            </Typography>
        );
    }

    return (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {regularFeatures.map((feature) => (
                <Chip key={feature} size="small" label={formatFeatureName(feature)} />
            ))}
        </Stack>
    );
}

function RetainerFlowValue({ features }: { features: string[] }) {
    const { translate } = useTranslate();
    const flow = selectedRetainerFlow(features);

    if (!flow) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('noRetainerFlow')}
            </Typography>
        );
    }

    return <Typography variant="body2">{formatRetainerFlowName(flow)}</Typography>;
}

function CountryChips({ codes }: { codes: readonly string[] | null | undefined }) {
    const { translate } = useTranslate();
    const enabled = normalizeEnabledCountryCodes(codes);

    if (!enabled.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                {translate('noCountriesEnabled')}
            </Typography>
        );
    }

    return (
        <Stack direction="row" sx={{ flexWrap: 'nowrap', gap: 0.5 }}>
            {enabled.map((code) => {
                const { shortLabel, Flag } = COUNTRY_MARKS[code];
                const label = translate(countryLabelKey(code));
                return (
                    <Tooltip key={code} title={label}>
                        <Box
                            component="span"
                            aria-label={label}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                height: 22,
                                pl: 0.25,
                                pr: 0.75,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper',
                                lineHeight: 1,
                            }}
                        >
                            <Box
                                component="span"
                                aria-hidden
                                sx={{ display: 'inline-flex', lineHeight: 0 }}
                            >
                                <Flag width="16" height="12" />
                            </Box>
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{ fontWeight: 700, letterSpacing: '0.04em' }}
                            >
                                {shortLabel}
                            </Typography>
                        </Box>
                    </Tooltip>
                );
            })}
        </Stack>
    );
}

function SupportEmailValue({ email }: { email: string | null | undefined }) {
    const { translate } = useTranslate();
    const value = email?.trim();

    return (
        <Typography variant="body2" color={value ? 'text.primary' : 'text.secondary'}>
            {value || translate('supportEmailNotConfigured')}
        </Typography>
    );
}

function RowActions({
    provider,
    handleAction,
}: {
    provider: ServiceProviderAdminRow;
    handleAction: ServiceProvidersTableProps['handleAction'];
}) {
    const { translate } = useTranslate();
    const supportEmailLabel = translate(
        provider.support_email ? 'editSupportEmail' : 'setSupportEmail'
    );

    return (
        <Stack direction="row" spacing={0.25} sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title={translate('manageFeatures')}>
                <IconButton
                    size="small"
                    color="primary"
                    sx={actionBtnSx}
                    aria-label={translate('manageFeatures')}
                    onClick={() => handleAction('FEATURES', provider)}
                >
                    <TuneOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title={translate('manageCountries')}>
                <IconButton
                    size="small"
                    color="primary"
                    sx={actionBtnSx}
                    aria-label={translate('manageCountries')}
                    onClick={() => handleAction('COUNTRIES', provider)}
                >
                    <PublicOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title={translate('manageDefaultConsultants')}>
                <IconButton
                    size="small"
                    sx={actionBtnSx}
                    aria-label={translate('manageDefaultConsultants')}
                    onClick={() => handleAction('DEFAULT_CONSULTANTS', provider)}
                >
                    <ManageAccountsOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            <Tooltip title={supportEmailLabel}>
                <IconButton
                    size="small"
                    sx={actionBtnSx}
                    aria-label={supportEmailLabel}
                    onClick={() => handleAction(ACTIONS.EDIT, provider)}
                >
                    <AlternateEmailOutlined sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    );
}

export function ServiceProvidersTable({
    providers,
    loading = false,
    handleAction,
}: ServiceProvidersTableProps) {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (loading) {
        return <TableSkeleton tableColumns={7} />;
    }

    if (!providers.length) {
        return (
            <Result
                icon={<BusinessOutlined sx={{ fontSize: 32 }} />}
                title={translate('noServiceProvidersFound')}
                description={translate('tryAdjustingSearch')}
            />
        );
    }

    if (isMobile) {
        return (
            <Stack spacing={1}>
                {providers.map((provider) => (
                    <Paper
                        key={provider.id}
                        variant="outlined"
                        sx={{ p: 1.5, borderRadius: 1.5, minWidth: 0 }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, wordBreak: 'break-word' }}
                        >
                            {provider.company_name}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center', mt: 0.25, minWidth: 0 }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                ID: {provider.id}
                            </Typography>
                            <SupportEmailValue email={provider.support_email} />
                        </Stack>
                        <Box sx={{ my: 1 }}>
                            <FeatureChips features={provider.enabled_features} />
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {translate('retainerFlow')}
                            </Typography>
                            <RetainerFlowValue features={provider.enabled_features} />
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <CountryChips codes={provider.enabled_country_codes} />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <RowActions provider={provider} handleAction={handleAction} />
                        </Box>
                    </Paper>
                ))}
            </Stack>
        );
    }

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderRadius: 1.5, overflowX: 'auto' }}
        >
            <Table
                size="small"
                sx={{
                    width: '100%',
                    minWidth: 1080,
                    '& .MuiTableCell-root': {
                        py: 0.75,
                        px: 1.25,
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 56 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '1%', whiteSpace: 'nowrap' }}>
                            {translate('company')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '22%' }}>
                            {translate('features')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>
                            {translate('retainerFlow')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '1%', whiteSpace: 'nowrap' }}>
                            {translate('countries')}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: '1%', whiteSpace: 'nowrap' }}>
                            {translate('supportEmail')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, width: 148 }} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {providers.map((provider) => (
                        <TableRow key={provider.id} hover>
                            <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                    {provider.id}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {provider.company_name}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ width: '22%' }}>
                                <FeatureChips features={provider.enabled_features} />
                            </TableCell>
                            <TableCell sx={{ minWidth: 240 }}>
                                <RetainerFlowValue features={provider.enabled_features} />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <CountryChips codes={provider.enabled_country_codes} />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <SupportEmailValue email={provider.support_email} />
                            </TableCell>
                            <TableCell align="right">
                                <RowActions provider={provider} handleAction={handleAction} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default ServiceProvidersTable;
